import type {
  Ability,
  Comprehension,
  PlannerSport,
  SessionPreferences,
} from "./types";
import { SPORT_CONFIGS, sportConfigForPlanner } from "./sport-config";
import type { WindDiscipline } from "../conditions/wind-scoring";
import {
  normaliseSupProfileKey,
  type SupProfileKey,
} from "../conditions/sport-profiles";

const ABILITIES: Ability[] = ["beginner", "intermediate", "advanced", "expert"];

export type BodyboardWavePreference = "unknown" | "shorebreak" | "hollow" | "mellow";
export type WindMode = WindDiscipline;
export type WindPreference = "light" | "moderate" | "strong" | "storm";
export type EquipmentRange = "unknown" | "light-wind" | "all-round" | "high-wind";
export type FoilMode = "prone" | "downwind" | "wing";
export type FoilBoardSetup = "unknown" | "stable" | "performance";
export type FoilSetup = "unknown" | "low-speed" | "all-round" | "high-speed";
export type FoilEnergyPreference = "flat" | "small" | "moderate";
export type PumpingAbility = "unknown" | "learning" | "competent" | "expert";
export type DivingMode = "recreational" | "drift" | "low-visibility";
export type DivingTraining = "not-specified" | "advanced" | "current-trained" | "low-visibility-trained";
export type DivingDepth = "shallow" | "moderate" | "deep";
export type DivingEntry = "shore" | "boat";
export type SupBoardType = "unknown" | "all-round" | "touring" | "surf" | "downwind";
export type SupCompetence = "unknown" | "learning" | "competent" | "advanced";
export type SupReadiness = "unknown" | "planned" | "confirmed";
export type SupAbility = "unknown" | Ability;
export type SupProfile = SupProfileKey;

export type SupPersonalFitContext = {
  kind: "sup";
  ability: SupAbility;
  profile: SupProfileKey;
  boardType: SupBoardType;
  boardVolumeL: number | null;
  riderMassKg: number | null;
  boardLengthFt: number | null;
  surfCompetence: SupCompetence;
  waveComfortMaxM: number | null;
  leash: SupReadiness;
  launchCompetence: SupCompetence;
  downwindCompetence: SupCompetence;
  safetyKit: SupReadiness;
  communications: SupReadiness;
  selfRescue: SupCompetence;
  routePlan: SupReadiness;
  recoveryPlan: SupReadiness;
  plannedDistanceKm: number | null;
  comfortableDistanceKm: number | null;
  routeBearingDeg: number | null;
  beachFacingDeg: number | null;
};

export type PlannerPreferenceOptions = {
  availableSupProfiles?: readonly SupProfileKey[];
};

export type PersonalFitContext =
  | {
      kind: "bodyboarding";
      ability: Ability;
      wavePreference: BodyboardWavePreference;
    }
  | {
      kind: "wind";
      ability: Ability;
      mode: WindMode;
      windPreference: WindPreference;
      equipmentRange: EquipmentRange;
    }
  | {
      kind: "foil";
      ability: Ability;
      mode: "prone";
      boardSetup: FoilBoardSetup;
      foilSetup: FoilSetup;
      energyPreference: FoilEnergyPreference;
      pumpingAbility: PumpingAbility;
      wingRange?: never;
    }
  | {
      kind: "foil";
      ability: Ability;
      mode: "downwind";
      boardSetup: FoilBoardSetup;
      foilSetup: FoilSetup;
      energyPreference: FoilEnergyPreference;
      wingRange?: never;
    }
  | {
      kind: "foil";
      ability: Ability;
      mode: "wing";
      boardSetup: FoilBoardSetup;
      foilSetup: FoilSetup;
      windPreference: Exclude<WindPreference, "storm">;
      wingRange: EquipmentRange;
    }
  | {
      kind: "diving";
      ability: Ability;
      mode: DivingMode;
      training: DivingTraining;
      plannedDepth: DivingDepth;
      entry: DivingEntry;
    }
  | SupPersonalFitContext;

function normaliseLegacyValue(
  sport: PlannerSport,
  key: string,
  value: unknown,
  namedSupAvailable: boolean,
): unknown {
  if (sport === "sup" && key === "profile") {
    if (!namedSupAvailable) return "sup";
    return typeof value === "string"
      ? normaliseSupProfileKey(value) ?? "sup-touring"
      : "sup-touring";
  }
  if (sport !== "foil" || key !== "discipline") return value;
  if (value === "surf" || value === "pump") return "prone";
  if (value === "kite") return "wing";
  return value;
}

function canonicalFinitePositive(value: unknown): string | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const numeric = typeof value === "number" ? value : Number(value.trim());
  return Number.isFinite(numeric) && numeric > 0 ? String(numeric) : null;
}

/**
 * Build a fully-valid preferences object for a sport, merging any supplied
 * raw values over the per-field defaults. Invalid or unknown values are
 * dropped in favour of the default. Throws for an unknown sport.
 */
export function coercePreferences(
  sport: string,
  raw: unknown,
  options: PlannerPreferenceOptions = {},
): SessionPreferences {
  const config = sportConfigForPlanner(sport, options.availableSupProfiles);
  const namedSupAvailable = config.sport === "sup" && config.scoringKeys[0] !== "sup";

  const rawValues =
    raw && typeof raw === "object" && "values" in raw
      ? ((raw as { values?: unknown }).values ?? {})
      : {};
  const supplied =
    rawValues && typeof rawValues === "object"
      ? (rawValues as Record<string, unknown>)
      : {};

  const values: Record<string, string> = {};
  for (const field of config.fields) {
    const suppliedValue = config.sport === "sup" && field.key === "profile"
      ? supplied.profile ?? (supplied.style === "touring" ? "sup" : undefined)
      : supplied[field.key];
    const candidate = normaliseLegacyValue(
      config.sport,
      field.key,
      suppliedValue,
      namedSupAvailable,
    );
    if (field.kind === "finite-positive-number") {
      values[field.key] = canonicalFinitePositive(candidate) ?? field.default;
      continue;
    }
    const valid =
      typeof candidate === "string" &&
      field.options.some((o) => o.value === candidate);
    values[field.key] = valid ? (candidate as string) : field.default;
  }

  return { sport: config.sport, values };
}

/** Convenience: the default preferences for a sport. */
export function defaultPreferences(sport: PlannerSport): SessionPreferences {
  return coercePreferences(sport, {});
}

/** Display register. Beginners and intermediates see plain language. */
export function comprehensionFor(ability: Ability): Comprehension {
  return ability === "advanced" || ability === "expert"
    ? "technical"
    : "plain";
}

export function prefAbility(prefs: SessionPreferences): Ability {
  const v = prefs.values.ability;
  return (ABILITIES as string[]).includes(v) ? (v as Ability) : "intermediate";
}

export function prefValue(prefs: SessionPreferences, key: string): string {
  return prefs.values[key] ?? "";
}

function oneOf<T extends string>(value: string | undefined, options: readonly T[], fallback: T): T {
  return options.includes(value as T) ? (value as T) : fallback;
}

function finitePositivePreference(
  values: Record<string, string>,
  key: string,
): number | null {
  const value = values[key];
  if (!value || value.trim() === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

/**
 * Resolve validated planner values into the typed personal-fit profile used by
 * sport-specific comfort evaluators. The persisted preference bag stays
 * backwards-compatible while condition logic no longer relies on free-form
 * string keys.
 */
export function personalFitContext(
  prefs: SessionPreferences
): PersonalFitContext | null {
  const ability = prefAbility(prefs);

  if (prefs.sport === "bodyboarding") {
    return {
      kind: "bodyboarding",
      ability,
      wavePreference: oneOf(
        prefs.values.waveType,
        ["unknown", "shorebreak", "hollow", "mellow"] as const,
        "unknown"
      ),
    };
  }

  if (prefs.sport === "wind") {
    return {
      kind: "wind",
      ability,
      mode: oneOf(
        prefs.values.discipline,
        ["kitesurf", "windsurf"] as const,
        "kitesurf"
      ),
      windPreference: oneOf(
        prefs.values.windRange,
        ["light", "moderate", "strong", "storm"] as const,
        "moderate"
      ),
      equipmentRange: oneOf(
        prefs.values.equipmentRange,
        ["unknown", "light-wind", "all-round", "high-wind"] as const,
        "unknown"
      ),
    };
  }

  if (prefs.sport === "foil") {
    const mode = oneOf(
      prefs.values.discipline,
      ["prone", "downwind", "wing"] as const,
      "wing"
    );
    const boardSetup = oneOf(
      prefs.values.boardSetup,
      ["unknown", "stable", "performance"] as const,
      "unknown"
    );
    const foilSetup = oneOf(
      prefs.values.foilSetup,
      ["unknown", "low-speed", "all-round", "high-speed"] as const,
      "unknown"
    );

    if (mode === "wing") {
      return {
        kind: "foil",
        ability,
        mode,
        boardSetup,
        foilSetup,
        windPreference: oneOf(
          prefs.values.windPref,
          ["light", "moderate", "strong"] as const,
          "moderate"
        ),
        wingRange: oneOf(
          prefs.values.wingRange,
          ["unknown", "light-wind", "all-round", "high-wind"] as const,
          "unknown"
        ),
      };
    }

    const energyPreference = oneOf(
      prefs.values.swellPref,
      ["none", "small", "moderate"] as const,
      "small"
    );
    const shared = {
      kind: "foil" as const,
      ability,
      mode,
      boardSetup,
      foilSetup,
      energyPreference: energyPreference === "none" ? "flat" as const : energyPreference,
    };
    if (mode === "prone") {
      return {
        ...shared,
        mode,
        pumpingAbility: oneOf(
          prefs.values.pumpingAbility,
          ["unknown", "learning", "competent", "expert"] as const,
          "unknown"
        ),
      };
    }
    return { ...shared, mode };
  }

  if (prefs.sport === "diving") {
    return {
      kind: "diving",
      ability,
      mode: oneOf(
        prefs.values.mode,
        ["recreational", "drift", "low-visibility"] as const,
        "recreational"
      ),
      training: oneOf(
        prefs.values.training,
        ["not-specified", "advanced", "current-trained", "low-visibility-trained"] as const,
        "not-specified"
      ),
      plannedDepth: oneOf(
        prefs.values.plannedDepth,
        ["shallow", "moderate", "deep"] as const,
        "moderate"
      ),
      entry: oneOf(prefs.values.entry, ["shore", "boat"] as const, "shore"),
    };
  }

  if (prefs.sport === "sup") {
    const profile = normaliseSupProfileKey(prefs.values.profile);
    if (!profile || prefs.values.profile === "sup") return null;
    const touring = profile === "sup-touring";
    const surf = profile === "sup-surf";
    const downwind = profile === "sup-downwind";
    return {
      kind: "sup",
      ability: touring
        ? oneOf(
            prefs.values.ability,
            ["unknown", "beginner", "intermediate", "advanced", "expert"] as const,
            "unknown",
          )
        : "unknown",
      profile,
      boardType: oneOf(
        prefs.values.boardType,
        ["unknown", "all-round", "touring", "surf", "downwind"] as const,
        "unknown",
      ),
      boardVolumeL: surf
        ? finitePositivePreference(prefs.values, "boardVolumeL")
        : null,
      riderMassKg: surf
        ? finitePositivePreference(prefs.values, "riderMassKg")
        : null,
      boardLengthFt: downwind
        ? finitePositivePreference(prefs.values, "boardLengthFt")
        : null,
      surfCompetence: surf
        ? oneOf(
            prefs.values.surfCompetence,
            ["unknown", "learning", "competent", "advanced"] as const,
            "unknown",
          )
        : "unknown",
      waveComfortMaxM: surf
        ? finitePositivePreference(prefs.values, "waveComfortMaxM")
        : null,
      leash: surf
        ? oneOf(
            prefs.values.leash,
            ["unknown", "planned", "confirmed"] as const,
            "unknown",
          )
        : "unknown",
      launchCompetence: surf
        ? oneOf(
            prefs.values.launchCompetence,
            ["unknown", "learning", "competent", "advanced"] as const,
            "unknown",
          )
        : "unknown",
      downwindCompetence: downwind
        ? oneOf(
            prefs.values.downwindCompetence,
            ["unknown", "learning", "competent", "advanced"] as const,
            "unknown",
          )
        : "unknown",
      safetyKit: surf
        ? oneOf(
            prefs.values.safetyKit,
            ["unknown", "planned", "confirmed"] as const,
            "unknown",
          )
        : "unknown",
      communications: downwind
        ? oneOf(
            prefs.values.communications,
            ["unknown", "planned", "confirmed"] as const,
            "unknown",
          )
        : "unknown",
      selfRescue: downwind
        ? oneOf(
            prefs.values.selfRescue,
            ["unknown", "learning", "competent", "advanced"] as const,
            "unknown",
          )
        : "unknown",
      routePlan: downwind
        ? oneOf(
            prefs.values.routePlan,
            ["unknown", "planned", "confirmed"] as const,
            "unknown",
          )
        : "unknown",
      recoveryPlan: downwind
        ? oneOf(
            prefs.values.recoveryPlan,
            ["unknown", "planned", "confirmed"] as const,
            "unknown",
          )
        : "unknown",
      plannedDistanceKm: touring || downwind
        ? finitePositivePreference(prefs.values, "plannedDistanceKm")
        : null,
      comfortableDistanceKm: touring || downwind
        ? finitePositivePreference(prefs.values, "comfortableDistanceKm")
        : null,
      routeBearingDeg: null,
      beachFacingDeg: null,
    };
  }

  return null;
}

/**
 * Resolve which underlying scoring key a sport's preferences map to. Only foil
 * branches by discipline today; every other sport has a single scoring key.
 */
export function prefScoringKey(prefs: SessionPreferences): string {
  if (prefs.sport === "foil") {
    switch (prefs.values.discipline) {
      case "wing":
        return "wing-foil";
      case "downwind":
        return "downwind-foil";
      case "prone":
      default:
        return "foil";
    }
  }
  if (prefs.sport === "sailing") {
    return prefs.values.boat === "keelboat" ? "sailing-club" : "sailing-hp";
  }
  if (prefs.sport === "sup") {
    if (prefs.values.profile === "sup") return "sup";
    return normaliseSupProfileKey(prefs.values.profile) ?? "sup";
  }
  return SPORT_CONFIGS[prefs.sport]?.scoringKeys[0] ?? prefs.sport;
}
